// =============================================================================
// MeuDriver — 2FA via TOTP (Supabase Auth MFA nativo)
// =============================================================================
// Usa supabase.auth.mfa.enroll / challenge / verify / unenroll.
// QR Code gerado localmente via qrcode.react (sem envio do secret para serviços externos).
// =============================================================================

import { useState, useEffect } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { ShieldCheck, ShieldOff, Loader2, KeyRound } from 'lucide-react'

// ---------------------------------------------------------------------------
// Tipos internos
// ---------------------------------------------------------------------------

type MfaStatus = 'loading' | 'inactive' | 'enrolling' | 'active'

interface EnrollState {
  factorId: string
  totpUri: string
  secret: string
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function TwoFactorSetupCard() {
  const [status, setStatus] = useState<MfaStatus>('loading')
  const [enroll, setEnroll] = useState<EnrollState | null>(null)
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)

  // Diálogo de confirmação para desativar
  const [unenrollDialogOpen, setUnenrollDialogOpen] = useState(false)
  const [unenrollFactorId, setUnenrollFactorId] = useState<string>('')
  const [unenrollCode, setUnenrollCode] = useState('')
  const [unenrolling, setUnenrolling] = useState(false)

  // ── Verifica se o usuário já tem MFA ativo ────────────────────────────────

  async function checkStatus() {
    setStatus('loading')
    try {
      const { data, error } = await supabase.auth.mfa.listFactors()
      if (error) throw error

      const totpFactors = data?.totp ?? []
      const verified = totpFactors.find((f) => f.status === 'verified')

      if (verified) {
        setUnenrollFactorId(verified.id)
        setStatus('active')
      } else {
        setStatus('inactive')
      }
    } catch {
      setStatus('inactive')
    }
  }

  useEffect(() => {
    checkStatus()
  }, [])

  // ── Iniciar processo de enroll ────────────────────────────────────────────

  async function startEnroll() {
    setLoading(true)
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        issuer: 'MeuDriver',
      })
      if (error) throw error

      setEnroll({
        factorId: data.id,
        totpUri: data.totp.uri,
        secret: data.totp.secret,
      })
      setCode('')
      setStatus('enrolling')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao iniciar 2FA.')
    } finally {
      setLoading(false)
    }
  }

  // ── Verificar código e ativar ─────────────────────────────────────────────

  async function verifyAndActivate() {
    if (!enroll) return
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      toast.error('Digite os 6 dígitos do código.')
      return
    }

    setLoading(true)
    try {
      // 1. Cria um challenge
      const { data: challenge, error: challengeErr } =
        await supabase.auth.mfa.challenge({ factorId: enroll.factorId })
      if (challengeErr) throw challengeErr

      // 2. Verifica o código
      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId: enroll.factorId,
        challengeId: challenge.id,
        code,
      })
      if (verifyErr) throw verifyErr

      toast.success('Autenticação em dois fatores ativada!')
      setEnroll(null)
      setCode('')
      await checkStatus()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Código inválido.'
      toast.error(msg.includes('Invalid') ? 'Código incorreto. Tente novamente.' : msg)
    } finally {
      setLoading(false)
    }
  }

  // ── Cancelar enroll ───────────────────────────────────────────────────────

  async function cancelEnroll() {
    if (!enroll) {
      setStatus('inactive')
      return
    }
    setLoading(true)
    try {
      await supabase.auth.mfa.unenroll({ factorId: enroll.factorId })
    } catch {
      // ignora erros de unenroll de factor não verificado
    } finally {
      setEnroll(null)
      setCode('')
      setStatus('inactive')
      setLoading(false)
    }
  }

  // ── Desativar 2FA ─────────────────────────────────────────────────────────

  async function handleUnenroll() {
    if (!unenrollFactorId) return
    if (!/^\d{6}$/.test(unenrollCode)) {
      toast.error('Digite o código de 6 dígitos do seu app autenticador.')
      return
    }

    setUnenrolling(true)
    try {
      // Precisa de um challenge+verify antes de poder fazer unenroll
      const { data: challenge, error: challengeErr } =
        await supabase.auth.mfa.challenge({ factorId: unenrollFactorId })
      if (challengeErr) throw challengeErr

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId: unenrollFactorId,
        challengeId: challenge.id,
        code: unenrollCode,
      })
      if (verifyErr) throw verifyErr

      const { error: unenrollErr } = await supabase.auth.mfa.unenroll({
        factorId: unenrollFactorId,
      })
      if (unenrollErr) throw unenrollErr

      toast.success('Autenticação em dois fatores desativada.')
      setUnenrollDialogOpen(false)
      setUnenrollCode('')
      await checkStatus()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao desativar 2FA.'
      toast.error(msg.includes('Invalid') ? 'Código incorreto.' : msg)
    } finally {
      setUnenrolling(false)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" />
            Autenticação em dois fatores (2FA)
          </CardTitle>
          {status === 'active' && (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
              <ShieldCheck className="h-3 w-3 mr-1" />
              Ativo
            </Badge>
          )}
          {status === 'inactive' && (
            <Badge variant="outline" className="text-muted-foreground">
              <ShieldOff className="h-3 w-3 mr-1" />
              Inativo
            </Badge>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Estado: carregando */}
          {status === 'loading' && (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Verificando configuração...
            </div>
          )}

          {/* Estado: inativo */}
          {status === 'inactive' && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Adicione uma camada extra de segurança à sua conta. Ao ativar, você precisará
                inserir um código gerado pelo seu app autenticador (Google Authenticator,
                Authy etc.) a cada login.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={startEnroll}
                disabled={loading}
                className="gap-2"
                data-testid="2fa-enable-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ShieldCheck className="h-4 w-4" />
                )}
                Ativar 2FA
              </Button>
            </div>
          )}

          {/* Estado: em processo de ativação */}
          {status === 'enrolling' && enroll && (
            <div className="space-y-5">
              <div>
                <p className="text-sm font-medium text-gray-800 mb-1">
                  1. Escaneie o QR Code com seu app autenticador
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  Use Google Authenticator, Authy, 1Password ou qualquer app compatível com TOTP.
                </p>
                <div className="flex flex-col items-start gap-4">
                  {/* QR Code gerado localmente — secret nunca sai do browser */}
                  <QRCodeSVG
                    value={enroll.totpUri}
                    size={200}
                    className="rounded-lg border bg-white p-2"
                  />
                  {/* Chave manual como fallback */}
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                      Não consegue escanear? Use a chave manual:
                    </p>
                    <code className="block text-xs bg-gray-100 rounded px-3 py-2 font-mono break-all select-all">
                      {enroll.secret}
                    </code>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-800">
                  2. Digite o código de 6 dígitos gerado pelo app
                </p>
                <Label htmlFor="totp-code" className="sr-only">
                  Código TOTP
                </Label>
                <Input
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-32 font-mono text-center text-lg tracking-widest"
                  autoComplete="one-time-code"
                  data-testid="totp-code-input"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={verifyAndActivate}
                  disabled={loading || code.length !== 6}
                  className="gap-2"
                  data-testid="2fa-verify-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="h-4 w-4" />
                  )}
                  Verificar e ativar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelEnroll}
                  disabled={loading}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {/* Estado: ativo */}
          {status === 'active' && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Sua conta está protegida com autenticação em dois fatores.
                A cada login, você precisará inserir o código do seu app autenticador.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setUnenrollCode(''); setUnenrollDialogOpen(true) }}
                className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
                data-testid="2fa-disable-btn"
              >
                <ShieldOff className="h-4 w-4" />
                Desativar 2FA
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de confirmação para desativar */}
      <Dialog open={unenrollDialogOpen} onOpenChange={setUnenrollDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ShieldOff className="h-5 w-5" />
              Desativar 2FA
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Para confirmar, insira o código atual do seu app autenticador.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="unenroll-code">Código TOTP</Label>
              <Input
                id="unenroll-code"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                placeholder="000000"
                value={unenrollCode}
                onChange={(e) => setUnenrollCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-32 font-mono text-center text-lg tracking-widest"
                autoComplete="one-time-code"
                data-testid="unenroll-code-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUnenrollDialogOpen(false)}
              disabled={unenrolling}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnenroll}
              disabled={unenrolling || unenrollCode.length !== 6}
              data-testid="unenroll-confirm-btn"
            >
              {unenrolling ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Desativando...</>
              ) : (
                'Confirmar desativação'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
